import React from "react";
import { Box, FormLabel, Typography } from "@mui/material";
import CustomLabel from "../custom-label";
import { FileUploadProps, UploadedFile } from "../../../types";
import FileItem from "./file-item";
import { Paperclip } from "lucide-react";
import useFileUpload from "@/modules/common/hooks/file-upload.hook";
import { useCallback, useState } from "react";
import { Upload } from "lucide-react";

export const FileUploadArea = ({
  setFiles,
  acceptedFileTypes = [],
  maxFileSize = 10 * 1024 * 1024,
  maxFiles = 100,
  onDeleteFile,
  label,
}: // uploadButtonType = "button",
FileUploadProps) => {
  const {
    uploadedFiles,
    fileError,
    handleFileChange,
    formatFileSize,
    processFiles,
    handleDeleteFile,
  } = useFileUpload({
    setFiles,
    acceptedFileTypes,
    maxFileSize,
    maxFiles,
    onDeleteFile,
  });
  const [isDragging, setIsDragging] = useState<boolean>(false);

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      const files = e.dataTransfer.files;
      if (files && files.length > 0) {
        processFiles(files);
      }
    },
    [processFiles]
  );
  return (
    <FileUpload
      setFiles={setFiles}
      onDeleteFile={onDeleteFile}
      label={label}
      acceptedFileTypes={acceptedFileTypes}
      maxFileSize={maxFileSize}
      maxFiles={maxFiles}
      uploadedFiles={uploadedFiles}
      fileError={fileError}
      handleDeleteFile={handleDeleteFile}
    >
      <Box
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        sx={(theme) => ({
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "90px",
          borderRadius: theme.nawy.radius.lg,
          minWidth: "100%",
          px: theme.nawy.spacing.md,
          py: theme.nawy.spacing.lg,
          backgroundColor:
            theme.nawy.colors.semantic.layer[
              isDragging ? "accentLight" : "primary"
            ],
          border: `2px dashed ${isDragging ? "#006694" : "#ccc"}`,
          transition: "all 0.3s ease",
        })}
      >
        <FormLabel
          sx={(theme) => ({
            cursor: "pointer",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: theme.nawy.spacing.sm,
            borderRadius: 1,
            width: "100%",
            minHeight: "40px",
            marginTop: 2,
          })}
        >
          <Upload />
          <Box
            sx={(theme) => ({
              display: "flex",
              flexDirection: "column",
              gap: theme.nawy.spacing.xxs,
              alignItems: "center",
            })}
          >
            <Typography
              sx={(theme) => theme.nawy.typography.english.variants.headline.xs}
            >
              Click here or drop files to upload
            </Typography>

            <Typography
              variant="caption"
              sx={(theme) => ({
                ...theme.nawy.typography.english.variants.body.sm.regular,
                color: theme.nawy.colors.semantic.text.secondary,
                textAlign: "center",
              })}
            >
              {acceptedFileTypes
                .map((type) => type.split("/").pop())
                .join(", ")}{" "}
              (max {formatFileSize(maxFileSize)})
            </Typography>
          </Box>
          <input
            type="file"
            id="actual-btn"
            multiple={maxFiles > 1}
            accept={acceptedFileTypes.join(",")}
            style={{ display: "none" }}
            onChange={handleFileChange}
          />
        </FormLabel>
      </Box>
    </FileUpload>
  );
};

export const FileUploadButton = ({
  setFiles,
  acceptedFileTypes = [],
  maxFileSize = 10 * 1024 * 1024,
  maxFiles = 100,
  onDeleteFile,
  label,
}: // uploadButtonType = "button",
FileUploadProps) => {
  const { handleFileChange, fileError, uploadedFiles, handleDeleteFile } =
    useFileUpload({
      setFiles,
      acceptedFileTypes,
      maxFileSize,
      maxFiles,
      onDeleteFile,
    });

  return (
    <FileUpload
      setFiles={setFiles}
      onDeleteFile={onDeleteFile}
      label={label}
      acceptedFileTypes={acceptedFileTypes}
      maxFileSize={maxFileSize}
      maxFiles={maxFiles}
      uploadedFiles={uploadedFiles}
      fileError={fileError}
      handleDeleteFile={handleDeleteFile}
    >
      <Box sx={{ display: "flex" }}>
        <FormLabel
          sx={(theme) => ({
            cursor: "pointer",
            display: "flex",
            gap: theme.nawy.spacing.xs,
            alignItems: "center",
            border: `1px solid ${theme.nawy.colors.semantic.border.primary}`,
            borderRadius: theme.nawy.radius.lg,
            px: theme.nawy.spacing.md,
            py: theme.nawy.spacing.xs,
            color: theme.nawy.colors.semantic.text.primary,
          })}
        >
          <Paperclip size={16} />

          <Typography
            sx={(theme) =>
              theme.nawy.typography.english.variants.body.md.regular
            }
          >
            Attach file
          </Typography>
          <input
            type="file"
            id="actual-btn"
            multiple={maxFiles > 1}
            accept={acceptedFileTypes.join(",")}
            style={{ display: "none" }}
            onChange={handleFileChange}
          />
        </FormLabel>
      </Box>
    </FileUpload>
  );
};

const FileUpload: React.FC<
  React.PropsWithChildren<
    FileUploadProps & {
      fileError: string | null;
      uploadedFiles: UploadedFile[];
      handleDeleteFile: (id: string) => void;
    }
  >
> = ({ children, ...rest }) => {
  const {
    label,
    maxFiles = 100,
    fileError,
    uploadedFiles,
    handleDeleteFile,
  } = rest;
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 0.5,
      }}
    >
      {label && (
        <CustomLabel
          sx={(theme) => theme.nawy.typography.english.variants.body.xs.regular}
        >
          {label}
        </CustomLabel>
      )}
      {!!uploadedFiles.length && (
        <Box
          sx={(theme) => ({
            border: `1px solid ${theme.nawy.colors.semantic.border.primary}`,
            borderRadius: theme.nawy.radius.lg,
          })}
        >
          {uploadedFiles.map((file, idx) => (
            <FileItem
              key={file.id}
              idx={idx}
              file={file}
              handleDeleteFile={handleDeleteFile}
            />
          ))}
        </Box>
      )}
      {uploadedFiles.length < maxFiles && children}

      {fileError && (
        <Typography sx={{ color: "red", fontSize: "12px", margin: "5px 0" }}>
          {fileError}
        </Typography>
      )}
    </Box>
  );
};